const SERVER_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTH_BASE = `${SERVER_BASE}/api/auth`;
const API_BASE = `${SERVER_BASE}/api/v1`;

// The CSRF token can't live in a plain readable cookie: the frontend (pages.dev) and backend
// (railway.app) are different origins, and a page can never read another origin's cookies via
// document.cookie — only the browser sends it automatically with requests. So instead the server
// hands the token back in the response body whenever a session is created or checked, we hold it
// here in memory, and echo it back as a header on every mutating request.
let csrfToken = null;

function captureCsrfToken(data) {
  if (data?.csrfToken) csrfToken = data.csrfToken;
  return data;
}

async function request(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (method !== 'GET' && method !== 'HEAD' && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers,
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const err = new Error(data?.error || `Request failed (${response.status})`);
    err.statusCode = response.status;
    err.details = data?.details;
    throw err;
  }
  return data;
}

export const authApi = {
  me: () => request(`${AUTH_BASE}/me`).then(captureCsrfToken),
  login: (email, password) =>
    request(`${AUTH_BASE}/login`, { method: 'POST', body: JSON.stringify({ email, password }) }).then(captureCsrfToken),
  register: (email, password, displayName) =>
    request(`${AUTH_BASE}/register`, {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName }),
    }).then(captureCsrfToken),
  logout: () => request(`${AUTH_BASE}/logout`, { method: 'POST' }).finally(() => { csrfToken = null; }),
  verifyMfa: (code) =>
    request(`${AUTH_BASE}/mfa/verify`, { method: 'POST', body: JSON.stringify({ code }) }).then(captureCsrfToken),
  setupMfa: () => request(`${AUTH_BASE}/mfa/setup`, { method: 'POST' }),
  enableMfa: (code) => request(`${AUTH_BASE}/mfa/enable`, { method: 'POST', body: JSON.stringify({ code }) }),
  disableMfa: (code) => request(`${AUTH_BASE}/mfa/disable`, { method: 'POST', body: JSON.stringify({ code }) }),
  changePassword: (currentPassword, newPassword) =>
    request(`${AUTH_BASE}/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  forgotPassword: (email) =>
    request(`${AUTH_BASE}/forgot-password`, { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, password) =>
    request(`${AUTH_BASE}/reset-password`, { method: 'POST', body: JSON.stringify({ token, password }) }),
  listSessions: () => request(`${AUTH_BASE}/sessions`),
  revokeSession: (id) => request(`${AUTH_BASE}/sessions/${id}`, { method: 'DELETE' }),
  revokeOtherSessions: () => request(`${AUTH_BASE}/sessions`, { method: 'DELETE' }),
  activity: (before) => request(`${AUTH_BASE}/activity${before ? `?before=${before}` : ''}`),
};

export const marketApi = {
  quote: (ticker) => request(`${API_BASE}/market/quote/${ticker}`),
  quotes: (tickers) => request(`${API_BASE}/market/quotes?tickers=${tickers.join(',')}`),
  history: (ticker, range = '3m', interval = '1D') =>
    request(`${API_BASE}/market/history/${ticker}?range=${range}&interval=${encodeURIComponent(interval)}`),
  search: (q) => request(`${API_BASE}/market/search?q=${encodeURIComponent(q)}`),
  overview: () => request(`${API_BASE}/market/overview`),
};

export const brokerApi = {
  status: () => request(`${API_BASE}/broker/status`),
  connect: (apiKey, apiSecret, environment) =>
    request(`${API_BASE}/broker/connect`, {
      method: 'POST',
      body: JSON.stringify({ apiKey, apiSecret, environment }),
    }),
  disconnect: () => request(`${API_BASE}/broker/connect`, { method: 'DELETE' }),
  account: () => request(`${API_BASE}/account`),
  accountHistory: (period = '1M', timeframe = '1D') =>
    request(`${API_BASE}/account/history?period=${period}&timeframe=${timeframe}`),
  positions: () => request(`${API_BASE}/positions`),
  orders: (status = 'all') => request(`${API_BASE}/orders?status=${status}`),
  placeOrder: (order) => request(`${API_BASE}/orders`, { method: 'POST', body: JSON.stringify(order) }),
  cancelOrder: (id) => request(`${API_BASE}/orders/${id}`, { method: 'DELETE' }),
};

export const watchlistApi = {
  list: () => request(`${API_BASE}/watchlist`),
  add: (ticker) => request(`${API_BASE}/watchlist`, { method: 'POST', body: JSON.stringify({ ticker }) }),
  remove: (ticker) => request(`${API_BASE}/watchlist/${ticker}`, { method: 'DELETE' }),
};

export const aiApi = {
  tradingSignal: (payload) =>
    request(`${API_BASE}/trading-signals`, { method: 'POST', body: JSON.stringify(payload) }),
  portfolioAnalysis: (payload) =>
    request(`${API_BASE}/portfolio-analysis`, { method: 'POST', body: JSON.stringify(payload) }),
  marketResearch: (payload) =>
    request(`${API_BASE}/market-research`, { method: 'POST', body: JSON.stringify(payload) }),
  tradeCheck: (payload) => request(`${API_BASE}/trade-check`, { method: 'POST', body: JSON.stringify(payload) }),
  portfolioHealth: (payload) =>
    request(`${API_BASE}/ai/portfolio-health`, { method: 'POST', body: JSON.stringify(payload) }),
  dailyBrief: () => request(`${API_BASE}/ai/daily-brief`),
  watchlistScan: (tickers) =>
    request(`${API_BASE}/ai/watchlist-scan`, { method: 'POST', body: JSON.stringify({ tickers }) }),
  portfolioManager: (payload) =>
    request(`${API_BASE}/ai/portfolio-manager`, { method: 'POST', body: JSON.stringify(payload) }),
};

export const riskApi = {
  sectorExposure: () => request(`${API_BASE}/risk/sector-exposure`),
  tradingPatterns: () => request(`${API_BASE}/risk/trading-patterns`),
};

export const stocksApi = {
  quote: (symbol) => request(`${API_BASE}/stocks/${symbol}/quote`),
  profile: (symbol) => request(`${API_BASE}/stocks/${symbol}/profile`),
  metrics: (symbol) => request(`${API_BASE}/stocks/${symbol}/metrics`),
  history: (symbol, range = '3m') => request(`${API_BASE}/stocks/${symbol}/history?range=${range}`),
  news: (symbol) => request(`${API_BASE}/stocks/${symbol}/news`),
  analysis: (symbol, payload) =>
    request(`${API_BASE}/stocks/${symbol}/analysis`, { method: 'POST', body: JSON.stringify(payload) }),
};

export const billingApi = {
  checkout: (plan) => request(`${API_BASE}/billing/checkout`, { method: 'POST', body: JSON.stringify({ plan }) }),
  status: () => request(`${API_BASE}/billing/status`),
};
